import { motion } from "framer-motion";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ContactInfoData } from "@/lib/validation";

interface ContactInfoStepProps {
  data: ContactInfoData;
  onDataChange: (data: ContactInfoData) => void;
  errors?: Record<string, string[]>;
}

const ContactInfoStep = ({
  data,
  onDataChange,
  errors,
}: ContactInfoStepProps) => {
  const updateField = (field: keyof ContactInfoData, value: string) => {
    onDataChange({ ...data, [field]: value });
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-6"
    >
      <div className="text-center mb-8">
        <h2 className="font-serif text-2xl md:text-3xl font-bold text-foreground mb-2">
          Your Contact Information
        </h2>
        <p className="text-muted-foreground">
          We'll use this to schedule your free in-home estimate.
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Full Name */}
        <div className="space-y-2 md:col-span-2">
          <Label htmlFor="fullName" className="text-base font-medium">
            Full Name
          </Label>
          <Input
            id="fullName"
            type="text"
            placeholder="John Smith"
            value={data.fullName}
            onChange={(e) => updateField("fullName", e.target.value)}
            className="h-12 text-base"
            autoComplete="name"
          />
          {errors?.fullName && (
            <p className="text-destructive text-sm">{errors.fullName[0]}</p>
          )}
        </div>

        {/* Email */}
        <div className="space-y-2">
          <Label htmlFor="email" className="text-base font-medium">
            Email Address
          </Label>
          <Input
            id="email"
            type="email"
            placeholder="john@example.com"
            value={data.email}
            onChange={(e) => updateField("email", e.target.value)}
            className="h-12 text-base"
            autoComplete="email"
          />
          {errors?.email && (
            <p className="text-destructive text-sm">{errors.email[0]}</p>
          )}
        </div>

        {/* Phone */}
        <div className="space-y-2">
          <Label htmlFor="phone" className="text-base font-medium">
            Phone Number
          </Label>
          <Input
            id="phone"
            type="tel"
            placeholder="(704) 555-1234"
            value={data.phone}
            onChange={(e) => updateField("phone", e.target.value)}
            className="h-12 text-base"
            autoComplete="tel"
          />
          {errors?.phone && (
            <p className="text-destructive text-sm">{errors.phone[0]}</p>
          )}
        </div>

        {/* Address */}
        <div className="space-y-2 md:col-span-2">
          <Label htmlFor="address" className="text-base font-medium">
            Street Address
          </Label>
          <Input
            id="address"
            type="text"
            placeholder="123 Main Street"
            value={data.address}
            onChange={(e) => updateField("address", e.target.value)}
            className="h-12 text-base"
            autoComplete="street-address"
          />
          {errors?.address && (
            <p className="text-destructive text-sm">{errors.address[0]}</p>
          )}
        </div>

        {/* City */}
        <div className="space-y-2">
          <Label htmlFor="city" className="text-base font-medium">
            City
          </Label>
          <Input
            id="city"
            type="text"
            placeholder="Charlotte"
            value={data.city}
            onChange={(e) => updateField("city", e.target.value)}
            className="h-12 text-base"
            autoComplete="address-level2"
          />
          {errors?.city && (
            <p className="text-destructive text-sm">{errors.city[0]}</p>
          )}
        </div>

        {/* ZIP Code */}
        <div className="space-y-2">
          <Label htmlFor="zipCode" className="text-base font-medium">
            ZIP Code
          </Label>
          <Input
            id="zipCode"
            type="text"
            placeholder="28277"
            value={data.zipCode}
            onChange={(e) => updateField("zipCode", e.target.value)}
            className="h-12 text-base"
            autoComplete="postal-code"
          />
          {errors?.zipCode && (
            <p className="text-destructive text-sm">{errors.zipCode[0]}</p>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default ContactInfoStep;
